import noop from '../../utils/noop';

interface BrazeSdkConfig {
  baseUrl: string;
  minimumIntervalBetweenTriggerActionsInSeconds: number;
  enableHtmlInAppMessages: boolean;
}

export default function init(
  brazeKey: string,
  url: string,
  callback: () => void = noop,
  options: { enableHtmlInAppMessagesInBrazeSdkConfig?: boolean } = {}
) {
  const { enableHtmlInAppMessagesInBrazeSdkConfig } = options;
  const brazeSdkConfig: BrazeSdkConfig = {
    baseUrl: url,
    minimumIntervalBetweenTriggerActionsInSeconds: 0,
    enableHtmlInAppMessages: !!enableHtmlInAppMessagesInBrazeSdkConfig,
  };

  const scriptUrl = 'https://js.appboycdn.com/web-sdk/4.10/braze.min.js';

  if (!brazeKey) {
    return;
  }

  interface BrazeWindow {
    braze?: {
      initialize: (key: string, config: BrazeSdkConfig) => boolean;
      automaticallyShowInAppMessages: () => void;
      openSession: () => void;
      [key: string]: any;
    };
    brazeQueue?: any[];
    rbiBrazeInit?: () => void;
    rbiBraze?: any;
  }

  (function (
    windowScope: BrazeWindow,
    documentScope: Document,
    scriptTagName: string,
    referenceElement?: HTMLScriptElement,
    newScriptElement?: HTMLScriptElement
  ) {
    windowScope.braze = {} as any;
    windowScope.brazeQueue = [];
    const methods = [
      'BrazeSdkMetadata',
      'DeviceProperties',
      'Card',
      'Card.prototype.dismissCard',
      'Card.prototype.removeAllSubscriptions',
      'Card.prototype.removeSubscription',
      'Card.prototype.subscribeToClickedEvent',
      'Card.prototype.subscribeToDismissedEvent',
      'Card.fromContentCardsJson',
      'Banner',
      'ImageOnly',
      'CaptionedImage',
      'ClassicCard',
      'ControlCard',
      'ContentCards',
      'ContentCards.prototype.getUnviewedCardCount',
      'Feed',
      'Feed.prototype.getUnreadCardCount',
      'ControlMessage',
      'InAppMessage',
      'InAppMessage.SlideFrom',
      'InAppMessage.ClickAction',
      'InAppMessage.DismissType',
      'InAppMessage.OpenTarget',
      'InAppMessage.ImageStyle',
      'InAppMessage.Orientation',
      'InAppMessage.TextAlignment',
      'InAppMessage.CropType',
      'InAppMessage.prototype.closeMessage',
      'InAppMessage.prototype.removeAllSubscriptions',
      'InAppMessage.prototype.removeSubscription',
      'InAppMessage.prototype.subscribeToClickedEvent',
      'InAppMessage.prototype.subscribeToDismissedEvent',
      'InAppMessage.fromJson',
      'FullScreenMessage',
      'ModalMessage',
      'HtmlMessage',
      'SlideUpMessage',
      'User',
      'User.Genders',
      'User.NotificationSubscriptionTypes',
      'User.prototype.addAlias',
      'User.prototype.addToCustomAttributeArray',
      'User.prototype.addToSubscriptionGroup',
      'User.prototype.getUserId',
      'User.prototype.getUserId',
      'User.prototype.incrementCustomUserAttribute',
      'User.prototype.removeFromCustomAttributeArray',
      'User.prototype.removeFromSubscriptionGroup',
      'User.prototype.setCountry',
      'User.prototype.setCustomLocationAttribute',
      'User.prototype.setCustomUserAttribute',
      'User.prototype.setDateOfBirth',
      'User.prototype.setEmail',
      'User.prototype.setEmailNotificationSubscriptionType',
      'User.prototype.setFirstName',
      'User.prototype.setGender',
      'User.prototype.setHomeCity',
      'User.prototype.setLanguage',
      'User.prototype.setLastKnownLocation',
      'User.prototype.setLastName',
      'User.prototype.setPhoneNumber',
      'User.prototype.setPushNotificationSubscriptionType',
      'InAppMessageButton',
      'InAppMessageButton.prototype.removeAllSubscriptions',
      'InAppMessageButton.prototype.removeSubscription',
      'InAppMessageButton.prototype.subscribeToClickedEvent',
      'FeatureFlag',
      'FeatureFlag.prototype.getStringProperty',
      'FeatureFlag.prototype.getNumberProperty',
      'FeatureFlag.prototype.getBooleanProperty',
      'automaticallyShowInAppMessages',
      'destroyFeed',
      'hideContentCards',
      'showContentCards',
      'showFeed',
      'showInAppMessage',
      'deferInAppMessage',
      'toggleContentCards',
      'toggleFeed',
      'changeUser',
      'destroy',
      'getDeviceId',
      'getDeviceId',
      'initialize',
      'isPushBlocked',
      'isPushPermissionGranted',
      'isPushSupported',
      'logCardClick',
      'logCardDismissal',
      'logCardImpressions',
      'logContentCardImpressions',
      'logContentCardClick',
      'logContentCardsDisplayed',
      'logCustomEvent',
      'logFeedDisplayed',
      'logInAppMessageButtonClick',
      'logInAppMessageClick',
      'logInAppMessageHtmlClick',
      'logInAppMessageImpression',
      'logPurchase',
      'openSession',
      'requestPushPermission',
      'removeAllSubscriptions',
      'removeSubscription',
      'requestContentCardsRefresh',
      'requestFeedRefresh',
      'refreshFeatureFlags',
      'requestImmediateDataFlush',
      'enableSDK',
      'isDisabled',
      'setLogger',
      'setSdkAuthenticationSignature',
      'addSdkMetadata',
      'disableSDK',
      'subscribeToContentCardsUpdates',
      'subscribeToFeedUpdates',
      'subscribeToInAppMessage',
      'subscribeToSdkAuthenticationFailures',
      'toggleLogging',
      'unregisterPush',
      'wipeData',
      'handleBrazeAction',
      'subscribeToFeatureFlagsUpdates',
      'getAllFeatureFlags',
      'logFeatureFlagImpression',
    ];

    for (let i = 0; i < methods.length; i++) {
      const methodName = methods[i];
      let currentObject: any = windowScope.braze;
      const pathParts = methodName.split('.');

      for (let j = 0; j < pathParts.length - 1; j++) {
        currentObject = currentObject[pathParts[j]];
      }

      // eslint-disable-next-line no-new-func
      currentObject[pathParts[pathParts.length - 1]] = new Function(
        'return function ' +
          methodName.replace(/\./g, '_') +
          '(){window.brazeQueue.push(arguments); return true}'
      )();
    }
    windowScope.braze!.getDeferredInAppMessage = function () {
      return new windowScope.braze!.InAppMessage();
    };
    windowScope.braze!.getCachedContentCards = function () {
      return new windowScope.braze!.ContentCards();
    };
    windowScope.braze!.getCachedFeed = function () {
      return new windowScope.braze!.Feed();
    };
    windowScope.braze!.getUser = function () {
      return new windowScope.braze!.User();
    };
    windowScope.braze!.getFeatureFlag = function () {
      return new windowScope.braze!.FeatureFlag();
    };
    (newScriptElement = documentScope.createElement(scriptTagName) as HTMLScriptElement).type =
      'text/javascript';
    // prevents One Trust from blocking the script
    newScriptElement.setAttribute('data-ot-ignore', '');
    newScriptElement.src = scriptUrl;
    newScriptElement.async = true;

    windowScope.rbiBrazeInit = () => {
      // Call this function again to get the success value that it return
      windowScope.rbiBraze = windowScope.braze;
      const success = windowScope.rbiBraze?.initialize(brazeKey, brazeSdkConfig);

      if (success) {
        windowScope.rbiBraze?.automaticallyShowInAppMessages();
        windowScope.rbiBraze?.openSession();

        callback();
      }
    };

    newScriptElement.onload = windowScope.rbiBrazeInit;

    referenceElement = documentScope.getElementsByTagName(scriptTagName)[0] as HTMLScriptElement;
    referenceElement.parentNode?.insertBefore(newScriptElement, referenceElement);
  })((window as unknown) as BrazeWindow, document, 'script');
}
